import React, { FC } from "react";
import {
  Container,
  NavItem,
  NavTitle,
  NavDesc,
  CreateWrapper,
  IconCreate,
  CreateLink,
  BackgroundIcon,
  ContentWrapper
} from "./styles";
import InstanceIcon from './figma-create-instance.svg'
import InstanceDarkIcon from './figma-create-instance-dark.svg'
import VolumesIcon from './figma-create-volume.svg'
import VolumesDarkIcon from './figma-create-volume-dark.svg'
import NetworksIcon from './figma-create-network.svg'
import NetworksDarkIcon from './figma-create-network-dark.svg'
import KubernetesIcon from './figma-create-kubernetes.svg'
import KubernetesDarkIcon from './figma-create-kubernetes-dark.svg'
import InstanceArtwork from './figma-instances.svg'
import InstanceArtworkDark from './figma-instances-dark.svg'
import VolumesArtwork from './figma-volumes.svg'
import VolumesArtworkDark from './figma-volumes-dark.svg'
import NetworksArtwork from './figma-networks.svg'
import NetworksArtworkDark from './figma-networks-dark.svg'
import KubernetesArtwork from './figma-kubernetes.svg'
import KubernetesArtworkDark from './figma-kubernetes-dark.svg'
import { useUi } from "../../../../context/ui.context";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context/user.context";
import { routePermissionSurfaces, usePermissionDecision } from '../../../../permissions';

type ItemType = {
  item:any
}

const HomeNavPanel:FC = () => {
  const [t] = useTranslation()
  const {user, quotas, appMenu} = useAuth()

  const navigate = useNavigate()
  const canCreateInstances = usePermissionDecision(routePermissionSurfaces.instanceCreate).allowed
  const canCreateVolumes = usePermissionDecision(routePermissionSurfaces.volumeCreate).allowed
  const canCreateNetworks = usePermissionDecision(routePermissionSurfaces.networkCreate).allowed
  const canCreateKubernetes = usePermissionDecision(routePermissionSurfaces.kubernetesCreate).allowed
  const {
    setViewModalComplete,
    setActiveAnimationComplete,
    viewAlert,
  } = useUi()
  const _createFuncInstance = () => {
    if(!user.user.active_client){
      navigate('/complete-registration')
    } else if(user.requires_email_verification){
      setViewModalComplete(true)
      setActiveAnimationComplete(true)
    } else if(quotas?.core?.instances?.limit === quotas?.core?.instances?.in_use){
      viewAlert({
        severity:'error',
        message:`${t('INSTANCE_QUOTA_EXCEEDED')}`,
      })
    } else {
      navigate('/instances/create?step=configure')
    }
  }
  const _createFuncVolume = () => {
    if(!user.user.active_client){
      navigate('/complete-registration')
    } else if(user.requires_email_verification){
      setViewModalComplete(true)
      setActiveAnimationComplete(true)
    } else if(quotas?.volume?.volumes?.limit === quotas?.volume?.volumes?.in_use){
      viewAlert({
        severity:'error',
        message:`${t('VOLUMES_QUOTA_EXCEEDED')}`,
      })
    } else {
      navigate('/volumes/create')
    }
  }
  const _createFuncNetwork = () => {
    if(!user.user.active_client){
      navigate('/complete-registration')
    } else if(user.requires_email_verification){
      setViewModalComplete(true)
      setActiveAnimationComplete(true)
    } else if(quotas?.network?.network?.limit === quotas?.network?.network?.in_use){
      viewAlert({
        severity:'error',
        message:`${t('NETWORK_QUOTA_EXCEEDED')}`,
      })
    } else {
      navigate('/networks/create')
    }
  }
  const _createFuncKubernetes = () => {
    if(!user.user.active_client){
      navigate('/complete-registration')
    } else if(user.requires_email_verification){
      setViewModalComplete(true)
      setActiveAnimationComplete(true)
    } else {
      navigate('/kubernetes/create')
    }
  }
  const data = [
    {
      title:t('INSTANCES'),
      desc:t("INSTANCE_NAV_DESC"),
      createText:t("CREATE_INSTANCE"),
      id:'1',
      icon:InstanceIcon,
      darkIcon:InstanceDarkIcon,
      artwork:InstanceArtwork,
      darkArtwork:InstanceArtworkDark,
      nav:() => _createFuncInstance(),
      slug:'openstack.instances',
    },
    {
      title:t('VOLUMES'),
      desc:t("VOLUME_NAV_DESC"),
      createText:t("CREATE_VOLUME"),
      id:'2',
      icon:VolumesIcon,
      darkIcon:VolumesDarkIcon,
      artwork:VolumesArtwork,
      darkArtwork:VolumesArtworkDark,
      nav:() => _createFuncVolume(),
      slug:'openstack.volumes',
    },
    {
      title:t('NETWORKS'),
      desc:t("NETWORK_NAV_DESC"),
      createText:t("CREATE_NETWORK"),
      id:'3',
      icon:NetworksIcon,
      darkIcon:NetworksDarkIcon,
      artwork:NetworksArtwork,
      darkArtwork:NetworksArtworkDark,
      nav:() => _createFuncNetwork(),
      slug:'openstack.networks',
    },
    {
      title:t('KUBERNETES_CLUSTERS'),
      desc:t('KUBERNETES_NAV_DESC'),
      createText:t('CREATE_KUBERNETES_CLUSTER'),
      id:'4',
      icon:KubernetesIcon,
      darkIcon:KubernetesDarkIcon,
      artwork:KubernetesArtwork,
      darkArtwork:KubernetesArtworkDark,
      nav:() => _createFuncKubernetes(),
      slug:'openstack.coe.clusters',
    }
  ]
  const visibleData = data.filter((item:any) => {
    if (!appMenu.includes(item.slug)) {
      return false
    }

    if (item.slug === 'openstack.instances') {
      return canCreateInstances
    }

    if (item.slug === 'openstack.volumes') {
      return canCreateVolumes
    }

    if (item.slug === 'openstack.networks') {
      return canCreateNetworks
    }

    if (item.slug === 'openstack.coe.clusters') {
      return canCreateKubernetes
    }

    return false
  })
  if(visibleData?.length === 0 ) return <div style={{height:"10px"}}/>
  return (
    <Container data-test-id='home-primary-services'>
      {visibleData.map((item) => (
        <Component key={item.id} item={item}/>
      ))}
    </Container>
  )
}


export default HomeNavPanel

const Component:FC<ItemType> = ({item}) => {
  const {themeDark} = useUi()
  return(
    <NavItem onClick={item?.nav} key={item.id}>
      <ContentWrapper>
        <div>
          <NavTitle>
            {item.title}
          </NavTitle>
          <NavDesc>
            {item.desc}
          </NavDesc>
        </div>
        <CreateWrapper>
          <IconCreate src={themeDark ? item.darkIcon : item.icon}/>
          <CreateLink>
            {item.createText}
          </CreateLink>
        </CreateWrapper>
      </ContentWrapper>
      <BackgroundIcon src={themeDark ? item.darkArtwork : item.artwork}/>
    </NavItem>
  )
}
